// ── Progressive node expansion logic ──

import { useCallback, useRef, useState } from "react";
import type { GraphData, GraphNode, GraphLink, NodeType } from "../types/graph";
import type { FullGraphResponse } from "../types/graph";
import { graphApi } from "../api/graphApi";
import { mergeGraphData, normalizeAreaLabel } from "./nodeExpandUtils";

/**
 * Cross-link generation disabled for performance.
 * Server-side enrichment (NEAR, SAME_DEFECT) from Neo4j provides
 * relationship discovery without the O(n²) client-side cost.
 * Re-enable by uncommenting if needed for small graphs.
 */
// function buildCrossLinks(nodes: GraphNode[]): GraphLink[] { ... }


/**
 * Manages the expanding/collapsing graph.
 * Starts with sites → click to load campaigns → areas → inspections.
 */
export function useNodeExpand() {
  const [graphData, setGraphData] = useState<GraphData>({
    nodes: [],
    links: [],
  });
  const [selectedNode, setSelectedNode] = useState<GraphNode | null>(null);
  const [loading, setLoading] = useState(false);
  const expandedIds = useRef(new Set<string>());

  /** Merge new nodes and links without duplicates */
  const merge = useCallback(
    (newNodes: GraphNode[], newLinks: GraphLink[]) => {
      setGraphData((prev) => mergeGraphData(prev, newNodes, newLinks));
    },
    [],
  );

  /** Collapse: remove all descendants of a node */
  const collapse = useCallback((nodeId: string) => {
    setGraphData((prev) => {
      // BFS to find all descendants
      const toRemove = new Set<string>();
      const queue = [nodeId];
      const childMap = new Map<string, string[]>();

      for (const link of prev.links) {
        const src =
          typeof link.source === "string" ? link.source : (link.source as unknown as GraphNode).id;
        const children = childMap.get(src) ?? [];
        const tgt =
          typeof link.target === "string" ? link.target : (link.target as unknown as GraphNode).id;
        children.push(tgt);
        childMap.set(src, children);
      }

      while (queue.length > 0) {
        const current = queue.shift()!;
        for (const child of childMap.get(current) ?? []) {
          toRemove.add(child);
          queue.push(child);
          expandedIds.current.delete(child);
        }
      }

      expandedIds.current.delete(nodeId);

      return {
        nodes: prev.nodes
          .filter((n) => !toRemove.has(n.id))
          .map((n) => (n.id === nodeId ? { ...n, expanded: false } : n)),
        links: prev.links.filter((l) => {
          const src =
            typeof l.source === "string" ? l.source : (l.source as unknown as GraphNode).id;
          const tgt =
            typeof l.target === "string" ? l.target : (l.target as unknown as GraphNode).id;
          return !toRemove.has(src) && !toRemove.has(tgt);
        }),
      };
    });
  }, []);

  /** Handle node click — expand or collapse */
  const onNodeClick = useCallback(
    async (node: GraphNode) => {
      setSelectedNode(node);

      // Inspections are leaf nodes — no expansion
      if (node.type === "inspection") return;

      // Already expanded → collapse
      if (expandedIds.current.has(node.id)) {
        collapse(node.id);
        return;
      }

      setLoading(true);
      try {
        const newNodes: GraphNode[] = [];
        const newLinks: GraphLink[] = [];

        if (node.type === "site") {
          const campaigns = await graphApi.getCampaigns(node.id);
          for (const c of campaigns) {
            newNodes.push({
              id: c.id,
              type: "campaign" as NodeType,
              label: c.period || `Campaign ${c.id.slice(0, 8)}`,
              severity: c.avgSeverity,
              childCount: c.inspectionCount,
              meta: {
                period: c.period,
                total: c.inspectionCount,
                avgSeverity: c.avgSeverity,
              },
            });
            newLinks.push({
              source: node.id,
              target: c.id,
              type: "HAS_CAMPAIGN",
            });
          }
        } else if (node.type === "campaign") {
          const areas = await graphApi.getAreas(node.id);
          for (const a of areas) {
            const areaLabel = normalizeAreaLabel(a.name);
            if (!areaLabel) continue;

            newNodes.push({
              id: a.id,
              type: "area" as NodeType,
              label: areaLabel,
              severity: a.avgSeverity,
              childCount: a.total,
              meta: {
                total: a.total,
                avgSeverity: a.avgSeverity,
                maxSeverity: a.maxSeverity,
              },
            });
            newLinks.push({
              source: node.id,
              target: a.id,
              type: "CONTAINS_AREA",
            });
          }
        } else if (node.type === "area") {
          // Need the parent campaign ID — stored in meta or find via links
          const parentLink = graphData.links.find((l) => {
            const tgt =
              typeof l.target === "string"
                ? l.target
                : (l.target as unknown as GraphNode).id;
            return tgt === node.id;
          });
          const campaignId = parentLink
            ? typeof parentLink.source === "string"
              ? parentLink.source
              : (parentLink.source as unknown as GraphNode).id
            : null;
          const areaLabel = normalizeAreaLabel(node.label);

          if (campaignId && areaLabel) {
            const inspections = await graphApi.getInspections(
              campaignId,
              areaLabel,
            );
            for (const insp of inspections) {
              newNodes.push({
                id: insp.id,
                type: "inspection" as NodeType,
                label: [insp.element, insp.rating].filter(Boolean).join(" — ") || insp.category || `#${insp.id.slice(0, 8)}`,
                severity: insp.severity,
                meta: {
                  rating: insp.rating,
                  priority: insp.priority,
                  category: insp.category,
                  element: insp.element,
                  area: insp.area,
                  description: insp.description,
                  inspector: insp.inspector,
                  createdAt: insp.createdAt,
                  photoIds: insp.photoIds,
                  recordId: insp.recordId,
                  defectCode: insp.defectCode ?? undefined,
                  impactType: insp.impactType ?? undefined,
                  riskScore: insp.riskScore ?? undefined,
                  riskLevel: insp.riskLevel ?? undefined,
                  rectificationPeriod: insp.rectificationPeriod ?? undefined,
                  dueDate: insp.dueDate ?? undefined,
                  defectType: insp.defectType ?? undefined,
                  geoArea: insp.geoArea ?? undefined,
                  trades: insp.trades,
                },
              });
              newLinks.push({
                source: node.id,
                target: insp.id,
                type: "HAS_INSPECTION",
              });
            }

            // Fetch semantic relationships (SAME_DEFECT, SAME_ASSET, NEAR, PRECEDED_BY) between these inspections
            // This connects "Steelwork" nodes to each other across campaigns, revealing patterns
            if (newNodes.length > 0) {
              const inspectionIds = inspections.map((i) => i.id);
              try {
                const semanticLinks = await graphApi.getRelationships(inspectionIds);
                for (const link of semanticLinks) {
                  newLinks.push({
                    source: link.source,
                    target: link.target,
                    type: link.type,
                    weight: link.weight,
                  });
                }
              } catch (err) {
                // If relationships endpoint fails, continue gracefully
                console.warn("Failed to fetch semantic relationships:", err);
              }
            }
          }
        }

        if (newNodes.length > 0) {
          merge(newNodes, newLinks);
          expandedIds.current.add(node.id);
          // Mark the clicked node as expanded
          setGraphData((prev) => ({
            nodes: prev.nodes.map((n) =>
              n.id === node.id ? { ...n, expanded: true } : n,
            ),
            links: prev.links,
          }));
        }
      } finally {
        setLoading(false);
      }
    },
    [graphData.links, merge, collapse],
  );

  /** Initialize with site nodes */
  const initSites = useCallback(
    (
      sites: Array<{
        id: string;
        name: string;
        client: string;
        campaignCount: number;
        avgSeverity: number;
      }>,
    ) => {
      const nodes: GraphNode[] = sites.map((s) => ({
        id: s.id,
        type: "site" as NodeType,
        label: s.name,
        severity: s.avgSeverity,
        childCount: s.campaignCount,
        meta: { client: s.client, total: s.campaignCount, avgSeverity: s.avgSeverity },
      }));
      setGraphData({ nodes, links: [] });
    },
    [],
  );

  /**
   * Auto-expand all nodes breadth-first until leaf (inspection) level.
   * Expands site → campaigns → areas → inspections progressively,
   * with settling pauses between levels so the force layout doesn't
   * get overwhelmed with too many clustered nodes at once.
   */
  const expandAll = useCallback(async () => {
    setLoading(true);
    try {
      // Helper: flush React state and read current graphData
      const readState = (): Promise<GraphData> =>
        new Promise((resolve) => {
          requestAnimationFrame(() => {
            setGraphData((prev) => {
              resolve(prev);
              return prev;
            });
          });
        });

      // Helper: wait for the force layout to partially settle
      const settleDelay = (ms: number) =>
        new Promise<void>((r) => setTimeout(r, ms));

      // Expand each level sequentially with settling pauses
      for (const expandType of ["site", "campaign", "area"] as NodeType[]) {
        const currentData = await readState();
        const toExpand = currentData.nodes.filter(
          (n) => n.type === expandType && !expandedIds.current.has(n.id),
        );

        // Expand nodes in larger batches — 3D WebGL handles the load
        const BATCH_SIZE = 15;
        for (let i = 0; i < toExpand.length; i += BATCH_SIZE) {
          const batch = toExpand.slice(i, i + BATCH_SIZE);
          for (const node of batch) {
            await onNodeClick(node);
          }
          // Yield between batches so the layout can absorb the new nodes
          await new Promise((r) => requestAnimationFrame(r));
        }

        // Brief settle pause between levels
        const nodeCount = (await readState()).nodes.length;
        const waitMs = Math.min(600, 100 + nodeCount);
        await settleDelay(waitMs);
      }
    } finally {
      setLoading(false);
    }
  }, [onNodeClick]);

  /**
   * Retract one level — collapse the deepest expanded node type.
   * If inspections are showing, removes them (collapses areas).
   * Then areas, then campaigns.
   */
  const retractOneLevel = useCallback(() => {
    setGraphData((prev) => {
      // Determine the deepest node type currently in the graph
      const typePriority: NodeType[] = ["inspection", "area", "campaign"];
      let deepestType: NodeType | null = null;
      for (const t of typePriority) {
        if (prev.nodes.some((n) => n.type === t)) {
          deepestType = t;
          break;
        }
      }
      if (!deepestType) return prev;

      // The parent type that expanded to create deepestType nodes
      const parentType: Record<string, NodeType> = {
        inspection: "area",
        area: "campaign",
        campaign: "site",
      };
      const pType = parentType[deepestType];
      if (!pType) return prev;

      // Find IDs of deepest-type nodes to remove
      const toRemove = new Set(
        prev.nodes.filter((n) => n.type === deepestType).map((n) => n.id),
      );

      // Un-expand the parents
      for (const n of prev.nodes) {
        if (n.type === pType) expandedIds.current.delete(n.id);
      }

      // Also clear expandedIds for the removed nodes
      for (const id of toRemove) expandedIds.current.delete(id);

      const baseLinks = prev.links.filter((l) => {
        const src = typeof l.source === "string" ? l.source : (l.source as unknown as GraphNode).id;
        const tgt = typeof l.target === "string" ? l.target : (l.target as unknown as GraphNode).id;
        return !toRemove.has(src) && !toRemove.has(tgt);
      });

      return {
        nodes: prev.nodes
          .filter((n) => !toRemove.has(n.id))
          .map((n) => (n.type === pType ? { ...n, expanded: false } : n)),
        links: baseLinks,
      };
    });
  }, []);

  /**
   * Expand a specific node (if not already expanded and not a leaf).
   */
  const expandNode = useCallback(
    async (node: GraphNode) => {
      if (node.type === "inspection") return;
      if (expandedIds.current.has(node.id)) return;
      await onNodeClick(node);
    },
    [onNodeClick],
  );

  /**
   * Bulk-load the entire graph from a single FullGraphResponse.
   * Replaces the sequential expandAll waterfall.
   */
  const initFullGraph = useCallback((resp: FullGraphResponse) => {
    const nodes: GraphNode[] = [];
    const links: GraphLink[] = [];

    // Site node
    const site = resp.site;
    nodes.push({
      id: site.id,
      type: "site" as NodeType,
      label: site.name,
      expanded: true,
      meta: { client: site.client },
    });
    expandedIds.current.add(site.id);

    // Campaign nodes
    for (const c of resp.campaigns) {
      nodes.push({
        id: c.id,
        type: "campaign" as NodeType,
        label: c.period,
        childCount: c.inspectionCount,
        expanded: true,
        meta: {
          total: c.inspectionCount,
          avgSeverity: c.avgSeverity,
          period: c.period,
        },
      });
      links.push({ source: site.id, target: c.id, type: "HAS_CAMPAIGN" });
      expandedIds.current.add(c.id);
    }

    // Area nodes
    for (const a of resp.areas) {
      const areaNodeId = `area::${a.campaignId}::${a.name}`;
      nodes.push({
        id: areaNodeId,
        type: "area" as NodeType,
        label: a.name,
        severity: a.avgSeverity,
        childCount: a.total,
        expanded: true,
        meta: {
          total: a.total,
          avgSeverity: a.avgSeverity,
          maxSeverity: a.maxSeverity,
          campaignId: a.campaignId,
        },
      });
      links.push({ source: a.campaignId, target: areaNodeId, type: "HAS_AREA" });
      expandedIds.current.add(areaNodeId);
    }

    // Inspection nodes
    for (const i of resp.inspections) {
      const areaLabel = normalizeAreaLabel(i.areaName);
      const parentId = areaLabel
        ? `area::${i.campaignId}::${areaLabel}`
        : i.campaignId;
      const linkType = areaLabel ? "CONTAINS" : "CONTAINS";

      nodes.push({
        id: i.id,
        type: "inspection" as NodeType,
        label: `${i.rating ?? "?"} — ${i.category ?? "Uncategorised"}`,
        severity: i.severity,
        meta: {
          rating: i.rating,
          priority: i.priority,
          category: i.category ?? undefined,
          element: i.element ?? undefined,
          area: i.areaName ?? undefined,
          description: i.description,
          inspector: i.inspector ?? undefined,
          createdAt: i.createdAt,
          photoIds: i.photoIds,
          recordId: i.id,
          status: i.status ?? undefined,
          lat: i.lat,
          lon: i.lon,
          campaignId: i.campaignId,
          period: i.period ?? undefined,
          defectCode: i.defectCode ?? undefined,
          impactType: i.impactType ?? undefined,
          riskScore: i.riskScore ?? undefined,
          riskLevel: i.riskLevel ?? undefined,
          rectificationPeriod: i.rectificationPeriod ?? undefined,
          dueDate: i.dueDate ?? undefined,
          defectType: i.defectType ?? undefined,
          defectGroup: i.defectGroup ?? undefined,
          geoArea: i.geoArea ?? undefined,
          trades: i.trades,
        },
      });
      links.push({ source: parentId, target: i.id, type: linkType });
    }

    // Semantic relationships
    for (const r of resp.relationships) {
      links.push({
        source: r.source,
        target: r.target,
        type: r.type,
        weight: r.weight,
      });
    }

    setGraphData({ nodes, links });
    setLoading(false);
  }, []);

  return {
    graphData,
    selectedNode,
    setSelectedNode,
    onNodeClick,
    initSites,
    initFullGraph,
    loading,
    expandAll,
    retractOneLevel,
    expandNode,
  };
}
